import * as React from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

import { withScrollTo } from 'components/ScrollToHOC';

import CampaignBlank from 'modules/campaigns/components/CampaignBlank';
import Deliverable from 'modules/deliverables/components/deliverable/Deliverable';
import DeliverableV2 from 'modules/deliverables/components/deliverable-v2/Deliverable';
import DeliverableContentContext, { PAGE_TYPE } from 'modules/deliverables/components/DeliverableContentContext';

import { __t, applyTranslations } from 'utils/language';

import './campaign-deliverables-list.scss';

import type {
  DeliverableCampaign,
  DeliverableUploadedEventAction,
  DeliverableV2TabKey,
  Subcampaign
} from 'types';

applyTranslations(require('./campaign-deliverables-list.i18n.json'));

type OwnProps = {
  campaignId: number,
  subcampaignsMapByInfluencer: {
    [x: number]: Subcampaign
  },
  deliverables: Array<DeliverableCampaign>,
  isDeliverablesLoading: boolean,
  expandedIdsMap: {
    [id: number]: boolean
  },
  selectedViewIdsMap: {
    [id: number]: DeliverableV2TabKey
  },
  contentApprovalV2: boolean,
  onContentMoved: () => void,
  onExpanded: (deliverableId: number) => void,
  onViewChange: (deliverableId: number, selectedView: DeliverableV2TabKey) => void,
  onTrackDeliverableUploadEvent: (eventAction: DeliverableUploadedEventAction) => void
};

type StateProps = {
  deliverablesWithCA2PerInflunecer: {
    [x: number]: Array<DeliverableCampaign>
  }
}

type Props = OwnProps & StateProps;

class CampaignDeliverablesList extends React.PureComponent<Props> {
  DeliverableWithScrollToV1 = withScrollTo(Deliverable);
  DeliverableWithScrollToV2 = withScrollTo(DeliverableV2, {
    suppressReplaceState: this.props.contentApprovalV2
  });

  render(): React.ReactNode {
    const {
      campaignId,
      deliverables,
      isDeliverablesLoading,
      expandedIdsMap,
      subcampaignsMapByInfluencer,
      selectedViewIdsMap,
      contentApprovalV2,
      deliverablesWithCA2PerInflunecer,
      onContentMoved,
      onViewChange,
      onExpanded,
      onTrackDeliverableUploadEvent
    } = this.props;

    const isNewDeliverable = !isEmpty(deliverables) && deliverables[0].contentApprovalV2;

    if (deliverables.length === 0) {
      const isLoadedEmpty = !isDeliverablesLoading && isEmpty(deliverables);

      return (
        <CampaignBlank>
          {isLoadedEmpty && __t('campaign_deliverables_list.no_deliverables')}
        </CampaignBlank>
      );
    }

    const DeliverableWithScrollTo = isNewDeliverable ? this.DeliverableWithScrollToV2 : this.DeliverableWithScrollToV1;

    return (
      <ul className="campaign-deliverables-list">
        {
          deliverables.map((deliverable: DeliverableCampaign) => {
            const influencerProfile = deliverable.creatorProfile;
            const subcampaign = influencerProfile ?
              subcampaignsMapByInfluencer[influencerProfile.id] : null;

            if (!subcampaign) {
              return null;
            }

            return (
              <li
                key={`campaign-deliverable-${deliverable.id}`}
                id={`deliverable-${deliverable.id}`}
                className="campaign-deliverables-list__item--v2"
              >
                <DeliverableContentContext.Provider value={PAGE_TYPE.CONTENT}>
                  <DeliverableWithScrollTo
                    influencerProfile={influencerProfile}
                    campaignId={campaignId}
                    deliverable={deliverable}
                    canEdit={false}
                    deliverablesWithCA2={deliverablesWithCA2PerInflunecer[influencerProfile?.id] || []}
                    subcampaign={subcampaign}
                    availableChannels={[]}
                    isForBrand
                    hasChanges={false}
                    inGlobalEditMode={false}
                    hash={`deliverable-${deliverable.id}`}
                    offerId={subcampaign.offer.id}
                    selectedTab={contentApprovalV2 ? selectedViewIdsMap[deliverable.id] : 'media'}
                    contentApprovalV2={contentApprovalV2}
                    isExpanded={expandedIdsMap[deliverable.id]}
                    onContentMoved={onContentMoved}
                    onExpanded={() => onExpanded(deliverable.id)}
                    onViewChange={(selectedView: DeliverableV2TabKey) => onViewChange(deliverable.id, selectedView)}
                    onTrackDeliverableUploadEvent={onTrackDeliverableUploadEvent}
                    isContentPage
                  />
                </DeliverableContentContext.Provider>
              </li>
            );
          })
        }
      </ul>
    );
  }
}

const mapStateToProps = ({ campaignContentApprovalDeliverables }): StateProps => ({
  deliverablesWithCA2PerInflunecer: campaignContentApprovalDeliverables.deliverables
});

export default connect(mapStateToProps)(CampaignDeliverablesList);
